import React, { useEffect, useRef, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { TbSortAscending2Filled } from "react-icons/tb";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import axios from "axios";
import { toast } from "react-toastify";

import styles from "./summary.module.scss";
import ViewSummaryPopup from "./viewSummaryPopup.js";
import GenerateCSV from "../../components/popup/generateCSV";
import { BsFiletypeCsv } from "react-icons/bs";
import Spinner from "../../components/spinner/index.js";
import AddedBy from "../../components/addedBy";
import { useLanguage } from "../../context/languageContext.js";
import OverflowTooltipCell from "../../components/tooltip/index.js";
import FilterBar from '../../components/filterBar';
import SortBy from '../../components/sortBy';
import { useFilterChips } from '../../components/filterBar/useFilterChips';
import Pagination from "../../components/pagination/index.js";
import { formatMongoDBTime } from '../../utills/dateAndTime';
import { formatAudioDuration, fetchAllGenresByLangOptions, htmlToPlainText , fetchAllActorNames } from '../../helper';
import { Tooltip } from 'react-tooltip';
import { Can } from "../../context/permissionContext";
import { SHOW_NOTABLE_QUOTES } from "./hiddenSections";



const BookWithSummary = () => {
  const { adminInfo, isShowCSVModalVisible, setIsShowCSVModalVisible, handleCloseCSV, handleGenerateCSV } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const apiParamsRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [allSummariesData, setAllSummariesData] = useState([]);
  const [selectedSummaryIds, setSelectedSummaryIds] = useState([]);
  const [isViewSummaryVisible, setIsViewSummaryVisible] = useState(false);
  const [selectedSummaryContent, setSelectedSummaryContent] = useState(null);
  // null = the whole summary; an array of section keys = just those.
  const [viewSummarySections, setViewSummarySections] = useState(null);
  // null = the popup is showing overview sections; an array = it is showing
  // chapters, fetched on open.
  const [viewChapters, setViewChapters] = useState(null);
  const [chaptersLoading, setChaptersLoading] = useState(false);


  // Pagination state. Seeded from the URL so returning from an edit lands the
  // lister back on the page they left, not on page 1 — with 831 summaries,
  // finding your way back to page 12 by hand is the whole afternoon.
  const [currentPage, setCurrentPage] = useState(() => {
    const fromUrl = Number(searchParams.get("page"));

    return Number.isInteger(fromUrl) && fromUrl > 0 ? fromUrl : 1;
  });
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalNumOfSummaries, setTotalNumOfSummaries] = useState(0);

  // Search state. Book name is the default field: it is what a lister has in
  // hand when they come looking for a row.

  // Sorting state
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  // The Sort by control. Sent on every request, so it is the list's order
  // of record; the column arrow below keeps itself in step with it.
  const [sortBy, setSortBy] = useState('latestCreated');

  const totalPages = Math.ceil(totalNumOfSummaries / itemsPerPage);


  // The chip row under the search box. `chipFilters` is already in the

  // shape the endpoint reads, so it merges straight into `apiFilters`.

  const filters = useFilterChips({ supportsMatchAny: true, stickyField: 'bookName' });

  // Genre is a managed list, so the filter offers the stored names rather than
  // asking anyone to spell them. Fetched once — 33 rows — and shared by the
  // search box and the Genre chip.
  const [genreNames, setGenreNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllGenresByLangOptions('en', adminInfo.token).then((genres) => {
      if (!cancelled) setGenreNames(genres.map((g) => g.genreName));
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);

  const apiFilters = {
    currentPage,
    itemsPerPage,
    langCode: "en",
    sortOrder: sortOrder,
    sortBy,
    // LAST on purpose: the single-field search above writes '' into every
    // field it is not searching, so a spread placed above it is wiped.
    ...filters.chipFilters,
    matchMode: filters.matchMode,
  };

  // The table's own columns, named as the headers name them. S.No, Overview,
  // Summary and Action are left out: they are a row number, two "Click to
  // View" links and the edit control — none of them is a value to match on.
  //
  // Two lists, because the two controls are not the same thing. The search box
  // types a value, so it can only offer fields you can type into; a chip picks
  // its value, so it can also offer the enumerated ones.

  // The names behind Created by / Edited by, so those filters are picked from
  // the team rather than spelled from memory.
  const [actorNames, setActorNames] = useState([]);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    fetchAllActorNames(adminInfo.token).then((names) => {
      if (!cancelled) setActorNames(names);
    });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);

  // Narration settings (speed, gaps, voice, model, build) as ONE filter: a
  // grouped list headed by each setting, every value with how many books it
  // covers, so every choice offered matches something. Picks are sent as
  // "listKey=value"; the server ORs picks of one setting and ANDs settings.
  // Null until the backend that serves them is deployed — the chip then
  // simply doesn't appear.
  const [audioSettingsFilter, setAudioSettingsFilter] = useState(null);

  useEffect(() => {
    if (!adminInfo?.token) return;
    let cancelled = false;
    axios
      .get(`${process.env.REACT_APP_API_ROOT}/api/admin/summary/audio-setting-options`, {
        headers: { Authorization: `Bearer ${adminInfo.token}` },
      })
      .then(({ data: response }) => {
        if (cancelled || !response?.status) return;
        const groups = Object.entries(response.data ?? {})
          .filter(([, spec]) => (spec?.values ?? []).length > 0)
          .map(([field, spec]) => {
            // "Audio sentence gap (ms)" → "Sentence gap", with "ms" on each value.
            const unit = /\(ms\)$/.test(spec.label) ? ' ms' : '';
            const bare = spec.label.replace(/^Audio /, '').replace(/ \(ms\)$/, '');
            const name = bare.charAt(0).toUpperCase() + bare.slice(1);
            return {
              label: name,
              options: spec.values.map((v) => ({
                value: `${field}=${v.value}`,
                // A build is a commit hash; seven characters identify it.
                label: `${name}: ${field === 'audioBuild' ? v.value.slice(0, 7) : v.value}${unit} (${v.books} ${v.books === 1 ? 'book' : 'books'})`,
              })),
            };
          });
        if (groups.length > 0) {
          setAudioSettingsFilter({
            value: 'audioSettings',
            label: 'Audio settings',
            suggestions: groups,
            // A pick names a setting AND a value; "is blank" has no setting
            // to apply to. "Not narrated" on the Audio chip covers it.
            noBlank: true,
          });
        }
      })
      .catch(() => {
        // Silent: an older backend has no such endpoint, and the list works
        // without this filter.
      });
    return () => { cancelled = true; };
  }, [adminInfo?.token]);

  // "20 Oct 2026" — the day narration was made, in the reader's timezone.
  const formatAudioDay = (iso) =>
    iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

  // "400/580 · 0.95×" — the pauses (sentence/paragraph) and speed it was
  // narrated with. Several values mean the chapters were made with different
  // settings, and showing all of them is the point.
  const formatAudioSettings = (audio) => {
    const join = (xs) => (Array.isArray(xs) && xs.length ? xs.join(" & ") : null);
    const sentence = join(audio?.sentenceGaps);
    const paragraph = join(audio?.paragraphGaps);
    const rate = join(audio?.speechRates);
    if (!sentence && !paragraph && !rate) return "";
    return [sentence || paragraph ? `${sentence ?? "?"}/${paragraph ?? "?"} ms` : null, rate ? `${rate}×` : null]
      .filter(Boolean)
      .join(" · ");
  };

  const searchFieldOptions = [
    { value: 'bookName', label: 'Book Name' },
    { value: 'authorName', label: 'Author Name' },
    { value: 'tagline', label: 'Tagline' },
    { value: 'genreName', label: 'Genre', suggestions: genreNames },
    { value: 'createdAt', label: 'Created on' },
    { value: 'updatedAt', label: 'Updated on' },
    { value: 'createdBy', label: 'Created by', suggestions: actorNames },
    { value: 'updatedBy', label: 'Edited by', suggestions: actorNames }
  ];

  const chipFilterOptions = [
    ...searchFieldOptions,
    {
      value: 'audioStatus',
      label: 'Audio',
      choices: [
        { value: 'none', label: 'Not narrated' },
        { value: 'partial', label: 'Partly narrated' },
        { value: 'all', label: 'Fully narrated' }
      ]
    },
    // When the narration was made — a day or a range, like Created on. A book
    // matches when any of its chapters was narrated in it.
    { value: 'audioGeneratedOn', label: 'Audio generated on' },
    ...(audioSettingsFilter ? [audioSettingsFilter] : []),
    {
      value: 'videoStatus',
      label: 'Video',
      choices: [
        { value: 'has', label: 'Has video' },
        { value: 'none', label: 'No video' }
      ]
    }
  ];

  // Changing a filter starts over at page 1 — but NOT on mount, which is what
  // made returning from an edit land on page 1 anyway: this effect runs with
  // the initial render and would clobber the page seeded from the URL.
  //
  // It compares the filters rather than skipping a first run, because a
  // "have I run yet" flag survives StrictMode's double-invoke: the first pass
  // would set it and the second would reset the page, putting the bug back in
  // development only. (StrictMode is off today — see src/index.js.)
  const lastFilters = useRef(null);

  useEffect(() => {
    // Named apart from the `filters` hook above: shadowing it meant this
    // guard never saw a chip change, so adding a filter left the reader
    // stranded on whatever page they were already on.
    const filterKey = JSON.stringify({
      itemsPerPage,
      sortOrder,
      sortBy,
      chips: filters.signature,
    });

    if (lastFilters.current === null) {
      lastFilters.current = filterKey;

      return;
    }

    if (lastFilters.current === filterKey) {
      return;
    }

    lastFilters.current = filterKey;
    setCurrentPage(1);
  }, [itemsPerPage, sortOrder, sortBy, filters.signature]);

  // Mirror the page into the URL — `replace` so it doesn't stack a history
  // entry per page turn, and page 1 stays a bare /summary.
  useEffect(() => {
    const params = new URLSearchParams(searchParams);

    if (currentPage > 1) {
      params.set("page", String(currentPage));
    } else {
      params.delete("page");
    }

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [currentPage, searchParams, setSearchParams]);

  useEffect(() => {
    if (!adminInfo?.token) {
      return;
    }

    document.title = "Penspace - Summaries";
    const keyValues = JSON.stringify({ currentPage, itemsPerPage, sortOrder, sortBy, filters: filters.signature });

    if (apiParamsRef.current === keyValues) {
      return;
    }

    apiParamsRef.current = keyValues;
    fetchAllSummariesData();
  }, [adminInfo, currentPage, itemsPerPage, sortOrder, sortBy, filters.signature]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };


  const handleSort = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
      // Keeps the arrow live now that Sort by decides the order.
      setSortBy(prev => (prev === 'oldestCreated' ? 'latestCreated' : 'oldestCreated'));
  };

  const fetchAllSummariesData = async () => {
    setLoading(true);

    try {
      const headers = { Authorization: adminInfo ? `Bearer ${adminInfo.token}` : null };
      const { data: response } = await axios.post(`${process.env.REACT_APP_API_ROOT}/api/admin/summary/getall`, { filters: apiFilters }, { headers });

      if (response.status) {
        const summariesData = response?.data || [];
        setAllSummariesData(summariesData.summaryDocs);
        setTotalNumOfSummaries(summariesData.totalCount || 0);

        // A `?page=` from before rows were deleted (or hand-typed) can point
        // past the end, which renders an empty table with no explanation.
        const lastPage = Math.ceil((summariesData.totalCount || 0) / itemsPerPage);

        if (lastPage > 0 && currentPage > lastPage) {
          setCurrentPage(lastPage);
        }
      };
    } catch (error) {
      console.error("fetchAllSummariesData catch error::", error);
    } finally {
      setTimeout(() => setLoading(false), 300);
    }
  };

  const handleSummarySelect = (seletedUserId) => {
    if (selectedSummaryIds.includes(seletedUserId)) {
      setSelectedSummaryIds((prevState) => prevState.filter(productId => productId !== seletedUserId));
    } else {
      setSelectedSummaryIds((prevState) => [...prevState, seletedUserId]);
    }
  };

  const handleDeleteSummaryData = async () => {
    try {
      const headers = { Authorization: `Bearer ${adminInfo.token}` };
      const response = await axios.put(`${process.env.REACT_APP_API_ROOT}/api/admin/summary/delete`, { summaryIds: selectedSummaryIds }, { headers });

      if (response.data.status) {
        setSelectedSummaryIds([]);
        fetchAllSummariesData();
        toast.success(response.data.message || "Summaries moved to the trash.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handlEditSummary = (summaryId) => {
    // Hand the edit screen the exact list URL to come back to. The page is in
    // the URL either way, but passing it means a direct link to an edit page
    // still has somewhere sensible to return to.
    navigate(`/summary/edit/${summaryId}`, {
      state: { returnTo: `${location.pathname}${location.search}` }
    });
  };

  const handleAddSummary = () => {
    navigate("/summary/add", {
      state: { returnTo: `${location.pathname}${location.search}` }
    });
  };

  // Summary is the CHAPTERS. They are not in the list's payload — a summary
  // carries every chapter's full text, and sending that for fifty rows is why
  // the list projects aggregates instead — so fetch the one summary here.
  const handleOpenViewSummary = async (summaryId) => {
    setSelectedSummaryContent(null);
    setViewSummarySections(null);
    setViewChapters([]);
    setChaptersLoading(true);
    setIsViewSummaryVisible(true);

    try {
      const { data: response } = await axios.get(
        `${process.env.REACT_APP_API_ROOT}/api/admin/summary/get/particular/${summaryId}`,
        { headers: { Authorization: `Bearer ${adminInfo?.token}` } }
      );

      const translations = response?.data?.translations || [];
      // The list asks for English, so show the chapters it was listing.
      const translation =
        translations.find((item) => item.langCode === "en") || translations[0];

      setViewChapters(translation?.chapters || []);
    } catch (error) {
      console.error("handleOpenViewSummary catch error::", error);
      toast.error("Could not load the chapters.");
      setViewChapters([]);
    } finally {
      setChaptersLoading(false);
    }
  };

  // Overview is every section the summary carries — About the Book, Why This
  // Book Matters, Key Ideas and the rest. It used to open narrowed to About
  // the Book alone, which is one of seven.
  const handleOpenViewOverview = (summaryContent) => {
    setViewChapters(null);
    setChaptersLoading(false);
    setSelectedSummaryContent(summaryContent || null);
    setViewSummarySections(null);
    setIsViewSummaryVisible(true);
  };

  const handleCloseViewSummaryModal = () => {
    setIsViewSummaryVisible(false);
    setSelectedSummaryContent(null);
    setViewSummarySections(null);
    setViewChapters(null);
    setChaptersLoading(false);
  };


  return (
    <React.Fragment>
      <div className="heading">
        <h4>
          {totalNumOfSummaries > 0 ? `Summaries (${totalNumOfSummaries})` : `Summary (${totalNumOfSummaries})`}
        </h4>
        <div className="d-flex gap-2">
          {selectedSummaryIds.length > 0 ? (
            <Can feature="summaries" action="delete"><button type="button" className="btn btn-outline-danger" onClick={handleDeleteSummaryData}>Delete</button></Can>
          ) : (
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-dark" onClick={() => setIsShowCSVModalVisible(true)}>Generate CSV <BsFiletypeCsv /></button>
              <Can feature="summaries" action="create"><button type="button" className="btn btn-dark" onClick={handleAddSummary}>Add Summary</button></Can>
            </div>
          )}
        </div>
      </div>
      <div className='d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center my-1'>
        <SortBy value={sortBy} onChange={setSortBy} />
        {totalPages > 1 && (
          <div className='d-flex justify-content-center justify-content-lg-end w-100 w-lg-auto ms-lg-auto'>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalNumOfSummaries}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showItemsPerPage={true}
              showTotalItems={true}
            />
          </div>
        )}
      </div>
      <FilterBar
        supportsMatchAny
      options={chipFilterOptions}
      chips={filters.chips}
      matchMode={filters.matchMode}
      onMatchModeChange={filters.setMatchMode}
      onAdd={filters.addChip}
      onUpdate={filters.updateChip}
      onRemove={filters.removeChip}
      onClear={filters.clearChips}
      />
      {loading ? (
        <Spinner />
      ) : (
        <div className="table_scrollbar table-responsive">
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.checkBox}>{""}</th>
                <th className={styles.index}>S.no</th>
                <th className={styles.bookName}>Book Name
                  <TbSortAscending2Filled 
                    size={18} 
                    style={{ cursor: 'pointer', marginLeft: '3px' }}
                    onClick={handleSort}
                    title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                  />
                </th>
                <th className={styles.author}>Author Name</th>
                <th className={styles.tagline}>Tagline</th>
                <th className={styles.genre}>Genre</th>
                {/* The app's own word for this blurb — its book page calls the
                    tab Overview, and this is what fills it. Click-to-view like
                    Summary beside it, rather than a truncated preview. */}
                <th className={styles.summaryContent}>Overview</th>
                <th className={styles.summaryContent}>Summary</th>
                <th className={styles.audio}>Audio</th>
                <th className={styles.audio}>Video</th>
                <th className={styles.createdAt}>Created on</th>
                <th className={styles.createdAt}>Updated on</th>
                <th>Added by</th>
                <th className={styles.action}>Action</th>
              </tr>
            </thead>
            <tbody>
              {allSummariesData?.length > 0 && (
                allSummariesData.map((category, index) => {
                  const isCategoriesSelected = selectedSummaryIds.some((id) => id === category._id);
                  const showSelectedLanguage = category.translations;
                  // Resolved server-side: the summary's own tagline, falling back
                  // to the book's. Stripped because a tagline copied in from a
                  // rich-text field can carry markup.
                  const taglineText = htmlToPlainText(showSelectedLanguage?.tagline || "");
                  const taglinePreview = taglineText.slice(0, 70).trim();
                  const summaryFullText = htmlToPlainText(showSelectedLanguage?.bookSummary || "");
                  // Any overview section with content is enough to open the
                  // popup — it shows all seven, so the first being empty says
                  // nothing about the other six.
                  const hasOverview = [
                    showSelectedLanguage?.bookSummary,
                    showSelectedLanguage?.whyItMatters,
                    showSelectedLanguage?.ideas,
                    // Hidden sections don't count: the popup wouldn't show them.
                    SHOW_NOTABLE_QUOTES ? showSelectedLanguage?.curatedQuotes : "",
                    showSelectedLanguage?.actionableInsights,
                    showSelectedLanguage?.shouldReadFor,
                    showSelectedLanguage?.reflectionQuestions,
                  ].some((value) => htmlToPlainText(value || "").length > 0);

                  // Narration coverage, computed server-side so the list does
                  // not have to carry chapter bodies just to count them.
                  const audioInfo = showSelectedLanguage?.audio;
                  const totalChapters = audioInfo?.chapterCount ?? 0;
                  const narratedChapters = audioInfo?.narratedChapters ?? 0;
                  const isGeneratedAudio = (audioInfo?.generatedChapters ?? 0) > 0;

                  const globalIndex = (currentPage - 1) * itemsPerPage + index;

                  return (
                    <tr key={category._id}>
                      <td className={styles.checkBox}>
                        <input
                          className={`${styles.box} form-check-input`}
                          type="checkbox"
                          checked={isCategoriesSelected}
                          onChange={() => handleSummarySelect(category._id)}
                        />
                      </td>
                      <td className={styles.index}>{globalIndex + 1}</td>
                      <td className={styles.bookName}>{showSelectedLanguage.bookName}</td>
                      <td className={styles.author}>
                        <ul className="pl-1 mb-0 list-unstyled">
                          {showSelectedLanguage.authorName?.map(item => (
                            <li className={styles.authorName} key={item.authorId}>{item.authorName}</li>
                          ))}
                        </ul>
                      </td>
                      <td className={styles.tagline}>
                        <span
                          data-tooltip-id={`tagline-tooltip-${category._id}`}
                          data-tooltip-content={taglineText}
                          data-tooltip-place="top"
                          className={styles.taglinePreview}
                        >
                          {taglinePreview || "-"}
                        </span>
                        {taglineText.length > taglinePreview.length && (
                          <Tooltip
                            className="custom-tooltip"
                            id={`tagline-tooltip-${category._id}`}
                            delayShow={200}
                            offset={10}
                            float={true}
                          />
                        )}
                      </td>
                      <td className={styles.genre}>
                        <ul className="pl-1 mb-0 list-unstyled">
                          {showSelectedLanguage.genre?.map(item => (
                            <li className={styles.catName} key={item.genreId}>{item.genreName}</li>
                          ))}
                        </ul>
                      </td>
                      {/* Overview = every section the summary carries. Offered
                          whenever ANY of them has content, not just the first:
                          gating on About the Book alone hid the rest when that
                          one section happened to be empty. */}
                      <td className={styles.summaryContent}>
                        {hasOverview ? (
                          <span
                            className={styles.viewSummaryTrigger}
                            onClick={() => handleOpenViewOverview(showSelectedLanguage)}
                          >
                            Click to View
                          </span>
                        ) : (
                          <span className={styles.audioNone}>-</span>
                        )}
                      </td>
                      {/* Summary = the chapters, fetched on click. The list
                          knows how many there are without carrying their text,
                          so a summary with none says so instead of opening an
                          empty popup. */}
                      <td className={styles.summaryContent}>
                        {totalChapters > 0 ? (
                          <span
                            className={styles.viewSummaryTrigger}
                            onClick={() => handleOpenViewSummary(category._id)}
                          >
                            Click to View
                          </span>
                        ) : (
                          <span className={styles.audioNone}>-</span>
                        )}
                      </td>
                      <td className={styles.audio}>
                        {narratedChapters === 0 ? (
                          <span className={styles.audioNone}>
                            {totalChapters === 0 ? "-" : "Not narrated"}
                          </span>
                        ) : (
                          <div className={styles.audioCell}>
                            <span className={narratedChapters === totalChapters ? styles.audioDone : styles.audioPartial}>
                              {narratedChapters}/{totalChapters} ch
                            </span>
                            <span className={styles.audioMeta}>
                              {formatAudioDuration(audioInfo?.narratedSeconds)}
                              {isGeneratedAudio ? " · AI" : " · uploaded"}
                            </span>
                            {audioInfo?.lastGeneratedAt && (
                              <span
                                className={styles.audioMeta}
                                title={`Narrated ${new Date(audioInfo.firstGeneratedAt || audioInfo.lastGeneratedAt).toLocaleString()} → ${new Date(audioInfo.lastGeneratedAt).toLocaleString()}`}
                              >
                                {formatAudioDay(audioInfo.firstGeneratedAt) &&
                                formatAudioDay(audioInfo.firstGeneratedAt) !== formatAudioDay(audioInfo.lastGeneratedAt)
                                  ? `${formatAudioDay(audioInfo.firstGeneratedAt)} – ${formatAudioDay(audioInfo.lastGeneratedAt)}`
                                  : formatAudioDay(audioInfo.lastGeneratedAt)}
                              </span>
                            )}
                            {formatAudioSettings(audioInfo) && (
                              <span className={styles.audioMeta} title="Pauses between sentences / paragraphs, and speech speed">
                                {formatAudioSettings(audioInfo)}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className={styles.audio}>
                        {showSelectedLanguage?.media?.video?.uploadedImageUrl ? (
                          <span>Video</span>
                        ) : (
                          <span className={styles.audioNone}>No video</span>
                        )}
                      </td>
                      {/* The Date filter searches `createdAt`, so the highlight
                          belongs on this cell — it used to mark the updated one,
                          which is a different date and never matched. */}
                      <td className={styles.createdAt}>{formatMongoDBTime(category.createdAt)}</td>
                      <td className={styles.createdAt}>{category.updatedAt ? formatMongoDBTime(category.updatedAt) : "—"}</td>
                      {/* <td className={styles.recommendedBy}>
                        <ul className="pl-1 mb-0 list-unstyled">
                          {summaryValuesByLang.recommendedBy?.map(item => (
                            <li className={styles.catName} key={item.recommenderId}>{item.recommenderName}</li>
                          ))}
                        </ul>
                      </td> */}
                      <td><AddedBy record={category} /></td>
                      <td className={styles.action}>
                        <div style={{ cursor: 'pointer' }} onClick={() => handlEditSummary(category._id)}><FaEdit size={20} /></div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <GenerateCSV
        title="summaries"
        isShowCSVModalVisible={isShowCSVModalVisible}
        handleCloseCSV={handleCloseCSV}
        handleGenerateCSV={() => handleGenerateCSV("summaries", { filters: apiFilters })}
      />
      {isViewSummaryVisible && (
        <ViewSummaryPopup
          isOpen={isViewSummaryVisible}
          onClose={handleCloseViewSummaryModal}
          summaryValues={selectedSummaryContent}
          only={viewSummarySections}
          chapters={viewChapters}
          loading={chaptersLoading}
          title={viewChapters || chaptersLoading ? "Summary" : "Overview"}
        />
      )}
    </React.Fragment>
  );
};

export default BookWithSummary;